import React, { useMemo, useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { useForecastContext_V2 } from '../../../context/ForecastContext_V2';
import { ForecastService } from '../../../services/api';
import '../ForecastGrid.css';

export function formatMonthNavitranso(rawMonth: string): string {
    if (!rawMonth) return '';
    const raw = rawMonth.trim();
    if (raw.toUpperCase().includes('TOTAL')) return raw;
    
    // Si viene formato ISO "YYYY-MM" (ej. "2027-01", "2027-12")
    const matchIso = raw.match(/^(\d{4})-(\d{2})$/);
    const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Dic'];
    if (matchIso) {
        const yearShort = matchIso[1].slice(-2);
        const monthNum = parseInt(matchIso[2], 10);
        if (monthNum >= 1 && monthNum <= 12) {
            return `${MESES[monthNum - 1]}-${yearShort}`;
        }
    }

    // Si viene formato texto tipo "ENE 2027", "ENE-2027", "ENE-27", "JAN 2027"
    const monthNamesMap: Record<string, string> = {
        'ENE': 'Ene', 'FEB': 'Feb', 'MAR': 'Mar', 'ABR': 'Abr', 'MAY': 'May', 'JUN': 'Jun',
        'JUL': 'Jul', 'AGO': 'Ago', 'SET': 'Set', 'SEP': 'Set', 'OCT': 'Oct', 'NOV': 'Nov', 'DIC': 'Dic',
        'JAN': 'Ene', 'APR': 'Abr', 'AUG': 'Ago', 'DEC': 'Dic'
    };
    const upper = raw.toUpperCase();
    for (const [key, val] of Object.entries(monthNamesMap)) {
        if (upper.includes(key)) {
            const yMatch = upper.match(/(\d{4}|\d{2})$/);
            const yy = yMatch ? yMatch[1].slice(-2) : '27';
            return `${val}-${yy}`;
        }
    }

    return raw;
}

const getClientColor = (name: string) => {
    if (name.includes('SPCC')) return 'bg-sky-700 text-white';
    if (name.includes('SPOT')) return 'bg-orange-500 text-white';
    return 'bg-petral-blue text-white';
};

const getRouteColor = (name: string) => {
    if (name.includes('MATARANI')) return 'bg-cyan-500 text-white';
    if (name.includes('MARCONA')) return 'bg-purple-500 text-white';
    if (name.includes('MEJILLONES')) return 'bg-fuchsia-500 text-white';
    if (name.includes('SPOT')) return 'bg-orange-500 text-white';
    return 'bg-slate-700 text-white';
};

const getVesselColor = (name: string) => {
    if (name.includes('TABLONES')) return 'bg-red-600 text-white';
    if (name.includes('MOQUEGUA')) return 'bg-green-600 text-white';
    if (name.includes('CONCON')) return 'bg-slate-600 text-white';
    if (name.includes('HUEMUL')) return 'bg-indigo-600 text-white';
    return 'bg-slate-100 text-slate-800 font-bold';
};

const getCellColor = (type: 'client' | 'route' | 'vessel' | undefined, name: string) => {
    if (!type) return '';
    if (type === 'client') return getClientColor(name);
    if (type === 'route') return getRouteColor(name);
    return getVesselColor(name);
};

const renderCellContent = (name: string, isSubtotal?: boolean) => {
    if (isSubtotal || !name) return name;
    const isBunk = name.includes('CALLAO(B)');
    const cleanKey = name.replace('-CALLAO(B)', '');
    const ports = cleanKey.split('-');

    if (ports.length > 2) {
        return (
            <div className="flex flex-col items-center justify-center gap-0.5 py-1" style={{ writingMode: 'horizontal-tb', transform: 'none' }}>
                <div className="flex flex-col items-center leading-tight font-extrabold text-[10px]">
                    {ports.map((p, idx) => (
                        <React.Fragment key={idx}>
                            <span>{p}</span>
                            {idx < ports.length - 1 && <span className="text-[8px] opacity-70">↓</span>}
                        </React.Fragment>
                    ))}
                </div>
                {isBunk && (
                    <span className="text-[8px] bg-amber-400 text-amber-950 px-1.5 py-0.5 rounded font-black tracking-tight shadow-sm whitespace-nowrap mt-0.5">
                        ⛽ CALLAO(b)
                    </span>
                )}
            </div>
        );
    }

    if (isBunk) {
        return (
            <div className="flex flex-col items-center justify-center gap-1 py-1" style={{ writingMode: 'horizontal-tb', transform: 'none' }}>
                <span className="font-extrabold">{cleanKey}</span>
                <span className="text-[8px] bg-amber-400 text-amber-950 px-1.5 py-0.5 rounded font-black tracking-tight shadow-sm whitespace-nowrap">
                    ⛽ CALLAO(b)
                </span>
            </div>
        );
    }
    return name;
};

const getColumnHeaderLabel = (type: 'client' | 'route' | 'vessel') => {
    if (type === 'client') return 'Cliente';
    if (type === 'route') return 'Ruta';
    return 'Buque';
};

interface FinancialMatrixNavitransoGridTableProps {
    data: any;
    months: string[];
    projectionLines?: any[];
}

export const FinancialMatrixNavitransoGridTable: React.FC<FinancialMatrixNavitransoGridTableProps> = ({
    data,
    months = [],
    projectionLines = []
}) => {
    const { 
        hiddenClients = [], 
        hiddenRoutes = [], 
        hiddenVessels = [], 
        hiddenMonths = [], 
        showSubtotals = true, 
        showAccumulatedTotal = true, 
        setProjectionLines = (() => {}),
        hideNaRows = false,
        handleFrequencyChange: onFrequencyChange,
        handleDeleteNode: onDeleteNode
    } = useForecastContext_V2() || {};

    const [expandedHire, setExpandedHire] = useState<Record<string, boolean>>({});
    const [expandedDemurrage, setExpandedDemurrage] = useState<Record<string, boolean>>({});
    const [expandedBunker, setExpandedBunker] = useState<Record<string, boolean>>({});
    const [expandedPortCosts, setExpandedPortCosts] = useState<Record<string, boolean>>({});

    const [groupOrder, setGroupOrder] = useState<('client' | 'route' | 'vessel')[]>(['client', 'route', 'vessel']);
    const [vesselsList, setVesselsList] = useState<any[]>([]);

    useEffect(() => {
        ForecastService.getVessels().then(vList => {
            setVesselsList(vList || []);
        }).catch(err => console.error("Error loading vessels in NavitransoGrid:", err));
    }, []);

    const handleVesselChange = (clientName: string, routeName: string, oldVesselName: string, newVesselId: string) => {
        const ports = routeName.split('-');
        if (ports.length < 2) return;
        const origin_port_id = ports[0];
        const destination_port_id = ports[1];
        const isTargetBunk = routeName.includes('CALLAO(B)');

        setProjectionLines(prev => {
            return prev.map(p => {
                const isLineBunk = p.puertosConfig?.some?.((pc: any) => pc.action === 'BUNKERING') || 
                                   p.quote_id?.includes?.('BUNKER') || 
                                   p.destination_port_id === 'CALLAO' ||
                                   p.origin_port_id === 'CALLAO';
                if (p.client_id === clientName && 
                    p.origin_port_id === origin_port_id && 
                    p.destination_port_id === destination_port_id && 
                    (isTargetBunk ? isLineBunk : !isLineBunk) &&
                    p.vessel_id === oldVesselName) {
                    return { ...p, vessel_id: newVesselId };
                }
                return p;
            });
        });
    };

    const handleGroupOrderSwap = (idx1: number, idx2: number) => {
        setGroupOrder(prev => {
            const next = [...prev];
            [next[idx1], next[idx2]] = [next[idx2], next[idx1]];
            return next;
        });
    };

    // Sort orders & Context Menu
    const [clientOrder, setClientOrder] = useState<string[]>([]);
    const [routeOrder, setRouteOrder] = useState<Record<string, string[]>>({});
    const [vesselOrder, setVesselOrder] = useState<Record<string, string[]>>({});
    const [contextMenu, setContextMenu] = useState<{ x: number, y: number, type: 'client'|'route'|'vessel', client?: string, route?: string, vessel?: string } | null>(null);

    useEffect(() => {
        const handleClick = () => setContextMenu(null);
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    const handleMove = (type: 'client' | 'route' | 'vessel', client?: string, route?: string, vessel?: string, dir?: 'up' | 'down') => {
        if (!dir) return;
        if (type === 'client') {
            const currentOrder = clientOrder.length > 0 ? [...clientOrder] : Object.keys(data.aggregated_data);
            const idx = currentOrder.indexOf(client!);
            if (idx === -1) return;
            const newIdx = dir === 'up' ? Math.max(0, idx - 1) : Math.min(currentOrder.length - 1, idx + 1);
            if (idx === newIdx) return;
            [currentOrder[idx], currentOrder[newIdx]] = [currentOrder[newIdx], currentOrder[idx]];
            setClientOrder(currentOrder);
        } else if (type === 'route') {
            const currentOrder = routeOrder[client!] && routeOrder[client!].length > 0 ? [...routeOrder[client!]] : Object.keys(data.aggregated_data[client!]);
            const idx = currentOrder.indexOf(route!);
            if (idx === -1) return;
            const newIdx = dir === 'up' ? Math.max(0, idx - 1) : Math.min(currentOrder.length - 1, idx + 1);
            if (idx === newIdx) return;
            [currentOrder[idx], currentOrder[newIdx]] = [currentOrder[newIdx], currentOrder[idx]];
            setRouteOrder(prev => ({ ...prev, [client!]: currentOrder }));
        } else if (type === 'vessel') {
            const routeKey = `${client}-${route}`;
            const currentOrder = vesselOrder[routeKey] && vesselOrder[routeKey].length > 0 ? [...vesselOrder[routeKey]] : Object.keys(data.aggregated_data[client!][route!]);
            const idx = currentOrder.indexOf(vessel!);
            if (idx === -1) return;
            const newIdx = dir === 'up' ? Math.max(0, idx - 1) : Math.min(currentOrder.length - 1, idx + 1);
            if (idx === newIdx) return;
            [currentOrder[idx], currentOrder[newIdx]] = [currentOrder[newIdx], currentOrder[idx]];
            setVesselOrder(prev => ({ ...prev, [routeKey]: currentOrder }));
        }
    };

    const rows = useMemo(() => {
        if (!data || !data.aggregated_data || !months || !Array.isArray(months) || months.length === 0) return [];
        
        const result: any[] = [];
        const sum = (arr: number[]) => arr.reduce((a, b) => a + (isNaN(b) ? 0 : b), 0);

        // 1. Aplanar las hojas del árbol original
        const flatLeaves: Array<{
            client: string;
            route: string;
            vessel: string;
            monthData: any;
        }> = [];

        Object.entries(data.aggregated_data).forEach(([client, routesData]: any) => {
            if (hiddenClients.includes(client)) return;
            Object.entries(routesData).forEach(([route, vesselsData]: any) => {
                if (hiddenRoutes.includes(route)) return;
                Object.entries(vesselsData).forEach(([vessel, monthData]: any) => {
                    if (hiddenVessels.includes(vessel)) return;
                    flatLeaves.push({ client, route, vessel, monthData });
                });
            });
        });

        // 2. Re-agrupar en base al orden dinámico especificado en groupOrder
        const regroupedTree: Record<string, Record<string, Record<string, any>>> = {};
        flatLeaves.forEach(({ client, route, vessel, monthData }) => {
            const keys = { client, route, vessel };
            const l1 = keys[groupOrder[0]];
            const l2 = keys[groupOrder[1]];
            const l3 = keys[groupOrder[2]];

            if (!regroupedTree[l1]) regroupedTree[l1] = {};
            if (!regroupedTree[l1][l2]) regroupedTree[l1][l2] = {};
            regroupedTree[l1][l2][l3] = monthData;
        });

        const getOriginalKeys = (l1: string, l2: string, l3: string) => {
            const keys: Record<string, string> = {};
            keys[groupOrder[0]] = l1;
            keys[groupOrder[1]] = l2;
            keys[groupOrder[2]] = l3;
            return {
                client: keys['client'],
                route: keys['route'],
                vessel: keys['vessel']
            };
        };

        const sortKeys = (keysList: string[], type: 'client' | 'route' | 'vessel', parentKey?: string) => {
            if (type === 'client' && clientOrder.length > 0) {
                return [...keysList].sort((a, b) => {
                    const idxA = clientOrder.indexOf(a);
                    const idxB = clientOrder.indexOf(b);
                    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                    return 0;
                });
            }
            if (type === 'route' && parentKey && routeOrder[parentKey] && routeOrder[parentKey].length > 0) {
                return [...keysList].sort((a, b) => {
                    const idxA = routeOrder[parentKey].indexOf(a);
                    const idxB = routeOrder[parentKey].indexOf(b);
                    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                    return 0;
                });
            }
            if (type === 'vessel' && parentKey && vesselOrder[parentKey] && vesselOrder[parentKey].length > 0) {
                return [...keysList].sort((a, b) => {
                    const idxA = vesselOrder[parentKey].indexOf(a);
                    const idxB = vesselOrder[parentKey].indexOf(b);
                    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                    return 0;
                });
            }
            return [...keysList].sort();
        };

        // Totales globales para el acumulado de flota
        const globalHire = new Array(months.length).fill(0);
        const globalVentaTerc = new Array(months.length).fill(0);
        const globalDemRev = new Array(months.length).fill(0);
        const globalIngPto = new Array(months.length).fill(0);
        const globalOtrosIng = new Array(months.length).fill(0);
        const globalVentas = new Array(months.length).fill(0);

        const globalCombustible = new Array(months.length).fill(0);
        const globalGastosPuerto = new Array(months.length).fill(0);
        const globalCostosDemora = new Array(months.length).fill(0);
        const globalComisiones = new Array(months.length).fill(0);
        const globalOtrosCostos = new Array(months.length).fill(0);
        const globalCostosDirectos = new Array(months.length).fill(0);

        const globalTce = new Array(months.length).fill(0);
        const globalArriendo = new Array(months.length).fill(0);
        const globalMargenBruto = new Array(months.length).fill(0);

        const level1List = sortKeys(Object.keys(regroupedTree), groupOrder[0]);

        level1List.forEach((level1Name) => {
            const level2Data = regroupedTree[level1Name];
            const level1RowSpanRef = { value: 0 };
            let isFirstLevel1Row = true;

            const level1Hire = new Array(months.length).fill(0);
            const level1VentaTerc = new Array(months.length).fill(0);
            const level1DemRev = new Array(months.length).fill(0);
            const level1IngPto = new Array(months.length).fill(0);
            const level1OtrosIng = new Array(months.length).fill(0);
            const level1Ventas = new Array(months.length).fill(0);

            const level1Combustible = new Array(months.length).fill(0);
            const level1GastosPuerto = new Array(months.length).fill(0);
            const level1CostosDemora = new Array(months.length).fill(0);
            const level1Comisiones = new Array(months.length).fill(0);
            const level1OtrosCostos = new Array(months.length).fill(0);
            const level1CostosDirectos = new Array(months.length).fill(0);

            const level1Tce = new Array(months.length).fill(0);
            const level1Arriendo = new Array(months.length).fill(0);
            const level1MargenBruto = new Array(months.length).fill(0);

            const level2List = sortKeys(Object.keys(level2Data), groupOrder[1], groupOrder[0] === 'client' ? level1Name : undefined);

            level2List.forEach((level2Name) => {
                const level3Data = level2Data[level2Name];
                const level2RowSpanRef = { value: 0 };
                let isFirstLevel2Row = true;

                let parentKeyLevel3: string | undefined = undefined;
                if (groupOrder[0] === 'client' && groupOrder[1] === 'route') {
                    parentKeyLevel3 = `${level1Name}-${level2Name}`;
                }
                const level3List = sortKeys(Object.keys(level3Data), groupOrder[2], parentKeyLevel3);

                level3List.forEach((level3Name) => {
                    const monthData = level3Data[level3Name];
                    const { client, route, vessel } = getOriginalKeys(level1Name, level2Name, level3Name);
                    const rowKey = `${client}-${route}-${vessel}`;

                    const trips = months.map(m => {
                        const line = projectionLines.find(p => 
                            p.client_id === client && 
                            `${p.origin_port_id}-${p.destination_port_id}` === route && 
                            p.vessel_id === vessel && 
                            p.month_index === m
                        );
                        return line ? (line.monthly_frequency || 0) : (monthData[m]?.freq !== undefined ? monthData[m]?.freq : 1);
                    });

                    // 1. VENTAS
                    const hire = months.map((m, i) => {
                        const mD = monthData[m] || {};
                        const freq = trips[i] || 0;
                        if (freq <= 0) return 0;
                        const uFreight = Number(mD.freight_revenue_unit ?? mD.gross_income_unit ?? ((mD.carga_unit || 13500) * (mD.flete_unit || 0)));
                        return uFreight > 0 ? (uFreight * freq) : Number(mD.freight_revenue || mD.gross_income || 0);
                    });
                    const ventaTerc = new Array(months.length).fill(0);
                    const demRev = months.map((m, i) => {
                        const mD = monthData[m] || {};
                        const freq = trips[i] || 0;
                        if (freq <= 0) return 0;
                        const uDem = mD.demurrage_revenue_unit ?? mD.demurrage_income_unit;
                        if (uDem !== undefined && uDem !== null && Number(uDem) > 0) {
                            return Number(uDem) * freq;
                        }
                        return Number(mD.demurrage_revenue || mD.demurrage_income || 0);
                    });
                    const ingPto = months.map((m, i) => {
                        const mD = monthData[m] || {};
                        const freq = trips[i] || 0;
                        if (freq <= 0) return 0;
                        const uMuell = mD.dockage_revenue_unit ?? mD.refacturacion_muellaje_unit;
                        if (uMuell !== undefined && uMuell !== null && Number(uMuell) > 0) {
                            return Number(uMuell) * freq;
                        }
                        return Number(mD.dockage_revenue || mD.refacturacion_muellaje || 0);
                    });
                    const otrosIng = new Array(months.length).fill(0);

                    const subtotalVentas = months.map((_, i) => hire[i] + ventaTerc[i] + demRev[i] + ingPto[i] + otrosIng[i]);

                    // 2. COSTOS DIRECTOS
                    const combustible = months.map((m, i) => {
                        const mD = monthData[m] || {};
                        const freq = trips[i] || 0;
                        if (freq <= 0) return 0;
                        const uBunk = mD.total_bunker_costs_unit ?? mD.bunker_costs_unit;
                        const val = (uBunk !== undefined && uBunk !== null && Number(uBunk) > 0)
                            ? Number(uBunk) * freq
                            : Number(mD.total_bunker_costs || mD.bunker_costs || 0);
                        return -val;
                    });
                    const gastosPuerto = months.map((m, i) => {
                        const mD = monthData[m] || {};
                        const freq = trips[i] || 0;
                        if (freq <= 0) return 0;
                        const uPort = mD.total_port_costs_unit ?? mD.port_costs_unit;
                        const val = (uPort !== undefined && uPort !== null && Number(uPort) > 0)
                            ? Number(uPort) * freq
                            : Number(mD.total_port_costs || mD.port_costs || 0);
                        return -val;
                    });
                    const costosDemora = months.map((m, i) => {
                        const mD = monthData[m] || {};
                        const freq = trips[i] || 0;
                        if (freq <= 0) return 0;
                        const uCostDem = mD.demurrage_hire_cost_unit;
                        const val = (uCostDem !== undefined && uCostDem !== null && Number(uCostDem) > 0)
                            ? Number(uCostDem) * freq
                            : Number(mD.demurrage_hire_cost || mD.costos_demora || 0);
                        return -val;
                    });
                    const comisiones = months.map((m, i) => {
                        const mD = monthData[m] || {};
                        const freq = trips[i] || 0;
                        if (freq <= 0) return 0;
                        const uComm = mD.total_commissions_unit;
                        const val = (uComm !== undefined && uComm !== null && Number(uComm) > 0)
                            ? Number(uComm) * freq
                            : Number(mD.total_commissions || mD.commissions_cost || 0);
                        return -val;
                    });
                    const otrosCostos = new Array(months.length).fill(0);

                    const subtotalCostosDirectos = months.map((_, i) => combustible[i] + gastosPuerto[i] + costosDemora[i] + comisiones[i] + otrosCostos[i]);

                    // 3. TIME CHARTER EQUIVALENT & MARGEN BRUTO
                    const tce = months.map((_, i) => subtotalVentas[i] + subtotalCostosDirectos[i]);
                    const arriendo = months.map((m, i) => {
                        const mD = monthData[m] || {};
                        const freq = trips[i] || 0;
                        if (freq <= 0) return 0;
                        const uChart = mD.charter_hire_cost_unit ?? mD.charter_hire_unit;
                        const val = (uChart !== undefined && uChart !== null && Number(uChart) > 0)
                            ? Number(uChart) * freq
                            : Number(mD.charter_hire_cost || mD.charter_hire || 0);
                        return -val;
                    });
                    const margenBruto = months.map((_, i) => tce[i] + arriendo[i]);

                    // Acumulaciones de niveles
                    hire.forEach((v, i) => { level1Hire[i] += v; globalHire[i] += v; });
                    ventaTerc.forEach((v, i) => { level1VentaTerc[i] += v; globalVentaTerc[i] += v; });
                    demRev.forEach((v, i) => { level1DemRev[i] += v; globalDemRev[i] += v; });
                    ingPto.forEach((v, i) => { level1IngPto[i] += v; globalIngPto[i] += v; });
                    otrosIng.forEach((v, i) => { level1OtrosIng[i] += v; globalOtrosIng[i] += v; });
                    subtotalVentas.forEach((v, i) => { level1Ventas[i] += v; globalVentas[i] += v; });

                    combustible.forEach((v, i) => { level1Combustible[i] += v; globalCombustible[i] += v; });
                    gastosPuerto.forEach((v, i) => { level1GastosPuerto[i] += v; globalGastosPuerto[i] += v; });
                    costosDemora.forEach((v, i) => { level1CostosDemora[i] += v; globalCostosDemora[i] += v; });
                    comisiones.forEach((v, i) => { level1Comisiones[i] += v; globalComisiones[i] += v; });
                    otrosCostos.forEach((v, i) => { level1OtrosCostos[i] += v; globalOtrosCostos[i] += v; });
                    subtotalCostosDirectos.forEach((v, i) => { level1CostosDirectos[i] += v; globalCostosDirectos[i] += v; });

                    tce.forEach((v, i) => { level1Tce[i] += v; globalTce[i] += v; });
                    arriendo.forEach((v, i) => { level1Arriendo[i] += v; globalArriendo[i] += v; });
                    margenBruto.forEach((v, i) => { level1MargenBruto[i] += v; globalMargenBruto[i] += v; });

                    // Construir array de métricas del nodo en formato Navitranso
                    const isExpHire = !!expandedHire[rowKey];
                    const isExpDem = !!expandedDemurrage[rowKey];
                    const isExpBunk = !!expandedBunker[rowKey];
                    const isExpPort = !!expandedPortCosts[rowKey];

                    const nodeMetrics: any[] = [];

                    // Fila 0: Frecuencia
                    nodeMetrics.push({
                        name: "Viajes (freq)",
                        values: trips,
                        total: sum(trips),
                        isCurrency: false,
                        isTotal: false,
                        isFrequencyEditable: true,
                        client, route, vessel,
                        rowKey
                    });

                    // Bloque 1: VENTAS
                    nodeMetrics.push({
                        name: "VENTAS",
                        values: subtotalVentas,
                        total: sum(subtotalVentas),
                        isCurrency: true,
                        isTotal: false,
                        isNavSubtotal: 'ventas'
                    });

                    nodeMetrics.push({
                        name: "  HIRE",
                        values: hire,
                        total: sum(hire),
                        isCurrency: true,
                        isTotal: false,
                        isExpandableNav: true,
                        navExpandKey: 'hire',
                        rowKey,
                        isExpanded: isExpHire
                    });
                    if (isExpHire) {
                        nodeMetrics.push({
                            name: "  ↳ Base Flete (TM x Tarifa)",
                            values: months.map((m, i) => (trips[i] > 0 ? (monthData[m]?.carga_unit || 13500) * trips[i] : 0)),
                            total: sum(months.map((m, i) => (trips[i] > 0 ? (monthData[m]?.carga_unit || 13500) * trips[i] : 0))),
                            isCurrency: false,
                            isSubRowMetric: true
                        });
                    }

                    if (!hideNaRows) {
                        nodeMetrics.push({
                            name: "  VENTA DE TERCEROS",
                            values: ventaTerc,
                            total: 0,
                            isCurrency: true,
                            isNaRow: true
                        });
                    }

                    nodeMetrics.push({
                        name: "  DEMORAS",
                        values: demRev,
                        total: sum(demRev),
                        isCurrency: true,
                        isTotal: false,
                        isExpandableNav: true,
                        navExpandKey: 'demurrage',
                        rowKey,
                        isExpanded: isExpDem
                    });
                    if (isExpDem) {
                        nodeMetrics.push({
                            name: "  ↳ Demurrage Revenue (Días x Rate)",
                            values: demRev,
                            total: sum(demRev),
                            isCurrency: true,
                            isSubRowMetric: true
                        });
                    }

                    nodeMetrics.push({
                        name: "  INGRESOS DE PUERTO",
                        values: ingPto,
                        total: sum(ingPto),
                        isCurrency: true,
                        isTotal: false
                    });

                    if (!hideNaRows) {
                        nodeMetrics.push({
                            name: "  OTROS INGRESOS",
                            values: otrosIng,
                            total: 0,
                            isCurrency: true,
                            isNaRow: true
                        });
                    }

                    // Bloque 2: COSTOS DIRECTOS
                    nodeMetrics.push({
                        name: "COSTOS DIRECTOS",
                        values: subtotalCostosDirectos,
                        total: sum(subtotalCostosDirectos),
                        isCurrency: true,
                        isTotal: false,
                        isNavSubtotal: 'costos'
                    });

                    nodeMetrics.push({
                        name: "  COMBUSTIBLE",
                        values: combustible,
                        total: sum(combustible),
                        isCurrency: true,
                        isTotal: false,
                        isExpandableNav: true,
                        navExpandKey: 'bunker',
                        rowKey,
                        isExpanded: isExpBunk
                    });
                    if (isExpBunk) {
                        nodeMetrics.push({
                            name: "  ↳ Búnker Mar (Navegación)",
                            values: combustible.map(v => v * 0.75),
                            total: sum(combustible) * 0.75,
                            isCurrency: true,
                            isSubRowMetric: true
                        });
                        nodeMetrics.push({
                            name: "  ↳ Búnker Puerto (Operación)",
                            values: combustible.map(v => v * 0.25),
                            total: sum(combustible) * 0.25,
                            isCurrency: true,
                            isSubRowMetric: true
                        });
                    }

                    nodeMetrics.push({
                        name: "  GASTOS DE PUERTO",
                        values: gastosPuerto,
                        total: sum(gastosPuerto),
                        isCurrency: true,
                        isTotal: false,
                        isExpandableNav: true,
                        navExpandKey: 'port',
                        rowKey,
                        isExpanded: isExpPort
                    });
                    if (isExpPort) {
                        nodeMetrics.push({
                            name: "  ↳ Agenciamiento POL + POD",
                            values: gastosPuerto,
                            total: sum(gastosPuerto),
                            isCurrency: true,
                            isSubRowMetric: true
                        });
                    }

                    nodeMetrics.push({
                        name: "  COSTOS DE DEMORA",
                        values: costosDemora,
                        total: sum(costosDemora),
                        isCurrency: true,
                        isTotal: false
                    });

                    nodeMetrics.push({
                        name: "  COMISIONES VARIAS",
                        values: comisiones,
                        total: sum(comisiones),
                        isCurrency: true,
                        isTotal: false
                    });

                    if (!hideNaRows) {
                        nodeMetrics.push({
                            name: "  OTROS COSTOS DIRECTOS",
                            values: otrosCostos,
                            total: 0,
                            isCurrency: true,
                            isNaRow: true
                        });
                    }

                    // Bloque 3: TIME CHARTER EQUIVALENT
                    nodeMetrics.push({
                        name: "TIME CHARTER EQUIVALENT",
                        values: tce,
                        total: sum(tce),
                        isCurrency: true,
                        isTotal: false,
                        isNavSubtotal: 'tce'
                    });

                    nodeMetrics.push({
                        name: "  COSTO DE ARRIENDO NAVES",
                        values: arriendo,
                        total: sum(arriendo),
                        isCurrency: true,
                        isTotal: false
                    });

                    // Bloque 4: MARGEN BRUTO
                    nodeMetrics.push({
                        name: "MARGEN BRUTO",
                        values: margenBruto,
                        total: sum(margenBruto),
                        isCurrency: true,
                        isTotal: true,
                        isNavSubtotal: 'margenBruto'
                    });

                    const vesselRowSpan = nodeMetrics.length;
                    level1RowSpanRef.value += vesselRowSpan;
                    level2RowSpanRef.value += vesselRowSpan;

                    nodeMetrics.forEach((metric, index) => {
                        result.push({
                            col1: isFirstLevel1Row && isFirstLevel2Row && index === 0 ? { type: groupOrder[0], name: level1Name, rowSpanRef: level1RowSpanRef } : null,
                            col2: isFirstLevel2Row && index === 0 ? { type: groupOrder[1], name: level2Name, rowSpanRef: level2RowSpanRef } : null,
                            col3: index === 0 ? { type: groupOrder[2], name: level3Name, rowSpan: vesselRowSpan } : null,
                            clientName: client,
                            routeName: route,
                            vesselName: vessel,
                            metric: metric,
                            isSubRow: !!metric.isSubRowMetric
                        });
                    });

                    isFirstLevel2Row = false;
                    isFirstLevel1Row = false;
                });
            });

            // Subtotal por cliente con Desglose Completo NAVITRANSO
            if (showSubtotals) {
                const subMetrics: any[] = [
                    // 1. VENTAS
                    { name: "VENTAS", values: level1Ventas, total: sum(level1Ventas), isCurrency: true, isTotal: false, isNavSubtotal: 'ventas' },
                    { name: "  HIRE", values: level1Hire, total: sum(level1Hire), isCurrency: true, isTotal: false },
                ];

                if (!hideNaRows) {
                    subMetrics.push({ name: "  VENTA DE TERCEROS", values: level1VentaTerc, total: sum(level1VentaTerc), isCurrency: true, isNaRow: true });
                }

                subMetrics.push(
                    { name: "  DEMORAS", values: level1DemRev, total: sum(level1DemRev), isCurrency: true, isTotal: false },
                    { name: "  INGRESOS DE PUERTO", values: level1IngPto, total: sum(level1IngPto), isCurrency: true, isTotal: false }
                );

                if (!hideNaRows) {
                    subMetrics.push({ name: "  OTROS INGRESOS", values: level1OtrosIng, total: sum(level1OtrosIng), isCurrency: true, isNaRow: true });
                }

                // 2. COSTOS DIRECTOS
                subMetrics.push(
                    { name: "COSTOS DIRECTOS", values: level1CostosDirectos, total: sum(level1CostosDirectos), isCurrency: true, isTotal: false, isNavSubtotal: 'costos' },
                    { name: "  COMBUSTIBLE", values: level1Combustible, total: sum(level1Combustible), isCurrency: true, isTotal: false },
                    { name: "  GASTOS DE PUERTO", values: level1GastosPuerto, total: sum(level1GastosPuerto), isCurrency: true, isTotal: false },
                    { name: "  COSTOS DE DEMORA", values: level1CostosDemora, total: sum(level1CostosDemora), isCurrency: true, isTotal: false },
                    { name: "  COMISIONES VARIAS", values: level1Comisiones, total: sum(level1Comisiones), isCurrency: true, isTotal: false }
                );

                if (!hideNaRows) {
                    subMetrics.push({ name: "  OTROS COSTOS DIRECTOS", values: level1OtrosCostos, total: sum(level1OtrosCostos), isCurrency: true, isNaRow: true });
                }

                // 3. TIME CHARTER EQUIVALENT
                subMetrics.push(
                    { name: "TIME CHARTER EQUIVALENT", values: level1Tce, total: sum(level1Tce), isCurrency: true, isTotal: false, isNavSubtotal: 'tce' },
                    { name: "  COSTO DE ARRIENDO NAVES", values: level1Arriendo, total: sum(level1Arriendo), isCurrency: true, isTotal: false }
                );

                // 4. MARGEN BRUTO
                subMetrics.push({
                    name: "MARGEN BRUTO",
                    values: level1MargenBruto,
                    total: sum(level1MargenBruto),
                    isCurrency: true,
                    isTotal: true,
                    isNavSubtotal: 'margenBruto'
                });

                level1RowSpanRef.value += subMetrics.length;
                const subtotalRouteRowSpanRef = { value: subMetrics.length };

                subMetrics.forEach((metric, index) => {
                    result.push({
                        col1: null,
                        col2: index === 0 ? { name: "Σ SUBTOTAL", rowSpanRef: subtotalRouteRowSpanRef, isSubtotal: true } : null,
                        col3: index === 0 ? { name: `TOTAL ${level1Name}`, rowSpan: subMetrics.length, isSubtotal: true } : null,
                        clientName: level1Name,
                        routeName: "",
                        vesselName: "",
                        metric: metric,
                        isSubRow: false,
                        isClientSubtotal: true
                    });
                });
            }
        });

        // Bloque Global de Flota Acumulada con Desglose Completo NAVITRANSO
        if (showAccumulatedTotal) {
            const globalMetrics: any[] = [
                // 1. VENTAS
                { name: "VENTAS CONSOLIDADAS", values: globalVentas, total: sum(globalVentas), isCurrency: true, isTotal: false, isNavSubtotal: 'ventas' },
                { name: "  HIRE", values: globalHire, total: sum(globalHire), isCurrency: true, isTotal: false },
            ];

            if (!hideNaRows) {
                globalMetrics.push({ name: "  VENTA DE TERCEROS", values: globalVentaTerc, total: sum(globalVentaTerc), isCurrency: true, isNaRow: true });
            }

            globalMetrics.push(
                { name: "  DEMORAS", values: globalDemRev, total: sum(globalDemRev), isCurrency: true, isTotal: false },
                { name: "  INGRESOS DE PUERTO", values: globalIngPto, total: sum(globalIngPto), isCurrency: true, isTotal: false }
            );

            if (!hideNaRows) {
                globalMetrics.push({ name: "  OTROS INGRESOS", values: globalOtrosIng, total: sum(globalOtrosIng), isCurrency: true, isNaRow: true });
            }

            // 2. COSTOS DIRECTOS
            globalMetrics.push(
                { name: "COSTOS DIRECTOS", values: globalCostosDirectos, total: sum(globalCostosDirectos), isCurrency: true, isTotal: false, isNavSubtotal: 'costos' },
                { name: "  COMBUSTIBLE", values: globalCombustible, total: sum(globalCombustible), isCurrency: true, isTotal: false },
                { name: "  GASTOS DE PUERTO", values: globalGastosPuerto, total: sum(globalGastosPuerto), isCurrency: true, isTotal: false },
                { name: "  COSTOS DE DEMORA", values: globalCostosDemora, total: sum(globalCostosDemora), isCurrency: true, isTotal: false },
                { name: "  COMISIONES VARIAS", values: globalComisiones, total: sum(globalComisiones), isCurrency: true, isTotal: false }
            );

            if (!hideNaRows) {
                globalMetrics.push({ name: "  OTROS COSTOS DIRECTOS", values: globalOtrosCostos, total: sum(globalOtrosCostos), isCurrency: true, isNaRow: true });
            }

            // 3. TIME CHARTER EQUIVALENT
            globalMetrics.push(
                { name: "TIME CHARTER EQUIVALENT", values: globalTce, total: sum(globalTce), isCurrency: true, isTotal: false, isNavSubtotal: 'tce' },
                { name: "  COSTO DE ARRIENDO NAVES", values: globalArriendo, total: sum(globalArriendo), isCurrency: true, isTotal: false }
            );

            // 4. MARGEN BRUTO
            globalMetrics.push({
                name: "MARGEN BRUTO",
                values: globalMargenBruto,
                total: sum(globalMargenBruto),
                isCurrency: true,
                isTotal: true,
                isNavSubtotal: 'margenBruto'
            });

            const globalRouteRowSpanRef = { value: globalMetrics.length };

            globalMetrics.forEach((metric, index) => {
                result.push({
                    col1: index === 0 ? { name: "TOTAL FLOTA", rowSpanRef: globalRouteRowSpanRef, isSubtotal: true, color: "bg-slate-800 text-white" } : null,
                    col2: null,
                    col3: null,
                    clientName: "TOTAL FLOTA",
                    routeName: "",
                    vesselName: "",
                    metric: metric,
                    isSubRow: false,
                    isGlobalTotal: true
                });
            });
        }

        return result;
    }, [data, months, projectionLines, groupOrder, clientOrder, routeOrder, vesselOrder, hiddenClients, hiddenRoutes, hiddenVessels, showSubtotals, showAccumulatedTotal, hideNaRows, expandedHire, expandedDemurrage, expandedBunker, expandedPortCosts]);

    const formatCurrency = (val: number | undefined | null) => {
        if (val === 0 || val === undefined || val === null || isNaN(val)) return "-";
        const isNeg = val < 0;
        const absFmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Math.abs(val));
        return isNeg ? `-${absFmt}` : absFmt;
    };

    const formatNumber = (val: number | undefined | null) => {
        if (val === 0 || val === undefined || val === null || isNaN(val)) return "-";
        return new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(val);
    };

    const toggleNavExpand = (key: 'hire'|'demurrage'|'bunker'|'port', rowKey: string) => {
        if (key === 'hire') setExpandedHire(p => ({ ...p, [rowKey]: !p[rowKey] }));
        if (key === 'demurrage') setExpandedDemurrage(p => ({ ...p, [rowKey]: !p[rowKey] }));
        if (key === 'bunker') setExpandedBunker(p => ({ ...p, [rowKey]: !p[rowKey] }));
        if (key === 'port') setExpandedPortCosts(p => ({ ...p, [rowKey]: !p[rowKey] }));
    };

    if (!data || !data.aggregated_data || projectionLines.length === 0 || rows.length === 0) {
        return (
            <div className="flex items-center justify-center h-64 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-slate-500">No hay proyecciones para mostrar. Usa el constructor de arriba.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2 relative">
            <div className="table-container shadow-xs border border-slate-200 rounded-lg overflow-auto max-h-[75vh] bg-white relative">
                <table id="forecast-grid-table" className="w-full text-sm text-left border-collapse">
                    <thead className="bg-slate-800 text-white uppercase font-semibold text-xs tracking-wider sticky top-0 z-20 shadow-xs">
                        <tr>
                            <th className="py-1 px-1 border border-slate-700 w-12 bg-slate-800 text-center font-bold text-[10px] tracking-normal">
                                <div className="flex items-center justify-center gap-0.5 min-w-[46px]">
                                    <span className="truncate">{getColumnHeaderLabel(groupOrder[0])}</span>
                                    <button 
                                        onClick={() => handleGroupOrderSwap(0, 1)} 
                                        className="text-slate-400 hover:text-white transition-colors cursor-pointer p-0.5 rounded hover:bg-slate-700 flex items-center justify-center"
                                        title="Mover a la derecha"
                                    >
                                        <ChevronRight size={12} />
                                    </button>
                                </div>
                            </th>
                            <th className="py-1 px-1 border border-slate-700 w-12 bg-slate-800 text-center font-bold text-[10px] tracking-normal">
                                <div className="flex items-center justify-center gap-0.5 min-w-[46px]">
                                    <button 
                                        onClick={() => handleGroupOrderSwap(1, 0)} 
                                        className="text-slate-400 hover:text-white transition-colors cursor-pointer p-0.5 rounded hover:bg-slate-700 flex items-center justify-center"
                                        title="Mover a la izquierda"
                                    >
                                        <ChevronLeft size={12} />
                                    </button>
                                    <span className="truncate">{getColumnHeaderLabel(groupOrder[1])}</span>
                                    <button 
                                        onClick={() => handleGroupOrderSwap(1, 2)} 
                                        className="text-slate-400 hover:text-white transition-colors cursor-pointer p-0.5 rounded hover:bg-slate-700 flex items-center justify-center"
                                        title="Mover a la derecha"
                                    >
                                        <ChevronRight size={12} />
                                    </button>
                                </div>
                            </th>
                            <th className="py-1 px-1 border border-slate-700 w-12 bg-slate-800 text-center font-bold text-[10px] tracking-normal">
                                <div className="flex items-center justify-center gap-0.5 min-w-[46px]">
                                    <button 
                                        onClick={() => handleGroupOrderSwap(2, 1)} 
                                        className="text-slate-400 hover:text-white transition-colors cursor-pointer p-0.5 rounded hover:bg-slate-700 flex items-center justify-center"
                                        title="Mover a la izquierda"
                                    >
                                        <ChevronLeft size={12} />
                                    </button>
                                    <span className="truncate">{getColumnHeaderLabel(groupOrder[2])}</span>
                                </div>
                            </th>
                            <th className="py-1 px-2 border border-slate-700 bg-slate-800 text-center font-bold text-xs tracking-wider w-36 min-w-[120px]">
                                Métrica
                            </th>
                            {months.filter(m => !hiddenMonths.includes(m)).map((m, idx) => (
                                <th key={idx} className="py-1 px-2 border border-slate-700 bg-slate-800 text-center font-extrabold text-xs tracking-wider min-w-[60px] w-16">{formatMonthNavitranso(m)}</th>
                            ))}
                            <th className="py-1 px-2 border border-sky-800 bg-sky-900 text-sky-100 text-center font-black text-[11px] tracking-wider min-w-[70px] w-20 shadow-2xs">TOTAL ACUM</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => {
                            const isVentas = row.metric.isNavSubtotal === 'ventas' || row.metric.name === 'VENTAS' || row.metric.name === 'VENTAS CONSOLIDADAS';
                            const isCostos = row.metric.isNavSubtotal === 'costos' || row.metric.name === 'COSTOS DIRECTOS';
                            const isTce = row.metric.isNavSubtotal === 'tce' || row.metric.name === 'TIME CHARTER EQUIVALENT';
                            const isMb = row.metric.isNavSubtotal === 'margenBruto' || row.metric.name === 'MARGEN BRUTO';

                            let rowStyleClass = 'hover:bg-slate-50';
                            if (row.isSubRow) rowStyleClass = 'bg-slate-50/60 text-xs text-slate-500';
                            if (row.metric.isNaRow) rowStyleClass = 'bg-slate-50/40 text-slate-400 text-xs';
                            if (isVentas || isCostos || isTce) rowStyleClass = 'bg-amber-100/90 font-bold text-slate-900 border-t border-amber-300';
                            if (isMb) rowStyleClass = 'bg-indigo-50/90 font-bold text-indigo-950 border-y border-indigo-200';
                            if (row.isClientSubtotal && !isVentas && !isCostos && !isTce && !isMb) rowStyleClass = 'bg-slate-100 font-bold text-slate-800';
                            if (row.isGlobalTotal && !isVentas && !isCostos && !isTce && !isMb) rowStyleClass = 'bg-slate-200 font-black text-slate-900';

                            return (
                                <tr key={i} className={`border border-slate-200 transition-colors ${rowStyleClass} ${row.metric.isTotal ? 'font-semibold' : ''}`}>
                                    {row.col1 && (
                                        <td rowSpan={row.col1.rowSpanRef ? row.col1.rowSpanRef.value : row.col1.rowSpan} colSpan={row.isGlobalTotal ? 3 : 1}
                                            onContextMenu={(e) => { 
                                                if (row.col1.type) {
                                                    e.preventDefault(); 
                                                    setContextMenu({ x: e.clientX, y: e.clientY, type: row.col1.type, client: row.clientName, route: row.routeName, vessel: row.vesselName }); 
                                                }
                                            }}
                                            className={`p-0 border border-slate-200 align-middle ${row.col1.color || getCellColor(row.col1.type, row.col1.name)} relative group cursor-context-menu`}>
                                            {!row.isGlobalTotal && row.col1.type && (
                                            <div className="absolute top-1 right-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button onClick={() => handleMove(row.col1.type, row.clientName, row.routeName, row.vesselName, 'up')} className="text-slate-300 hover:text-white"><ChevronUp size={14} /></button>
                                                <button onClick={() => handleMove(row.col1.type, row.clientName, row.routeName, row.vesselName, 'down')} className="text-slate-300 hover:text-white"><ChevronDown size={14} /></button>
                                            </div>
                                            )}
                                            {row.col1.type === 'vessel' && !row.isGlobalTotal ? (
                                                <div className="w-full h-full flex items-center justify-center relative min-h-[60px]">
                                                    <div className="vertical-text mx-auto px-2 pointer-events-none text-white font-extrabold text-xs">
                                                        {row.col1.name}
                                                    </div>
                                                    <select
                                                        value={row.col1.name}
                                                        onChange={(e) => handleVesselChange(row.clientName, row.routeName, row.col1.name, e.target.value)}
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                    >
                                                        {vesselsList.map(v => (
                                                            <option key={v.vessel_id} value={v.vessel_id} className="bg-slate-800 text-white text-xs">
                                                                {v.vessel_id}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : (
                                                <div className={`vertical-text mx-auto px-2 ${row.isGlobalTotal ? 'text-sm tracking-wider transform rotate-0 writing-mode-unset flex items-center justify-center h-full font-bold' : ''}`} style={row.isGlobalTotal ? { writingMode: 'unset', transform: 'none' } : {}}>{renderCellContent(row.col1.name, row.isGlobalTotal)}</div>
                                            )}
                                        </td>
                                    )}
                                    {row.col2 && (
                                        <td rowSpan={row.col2.rowSpanRef ? row.col2.rowSpanRef.value : row.col2.rowSpan} 
                                            onContextMenu={(e) => { 
                                                if (!row.col2.isSubtotal && row.col2.type) { 
                                                    e.preventDefault(); 
                                                    setContextMenu({ x: e.clientX, y: e.clientY, type: row.col2.type, client: row.clientName, route: row.routeName, vessel: row.vesselName }); 
                                                } 
                                            }}
                                            className={`p-0 border border-slate-200 align-middle relative group ${row.col2.isSubtotal ? 'bg-slate-800 text-amber-400 font-bold' : getCellColor(row.col2.type, row.col2.name) + ' cursor-context-menu'}`}>
                                            {!row.col2.isSubtotal && row.col2.type && (
                                                <div className="absolute top-1 right-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                                    <button onClick={() => handleMove(row.col2.type, row.clientName, row.routeName, row.vesselName, 'up')} className="text-slate-400 hover:text-white"><ChevronUp size={14} /></button>
                                                    <button onClick={() => handleMove(row.col2.type, row.clientName, row.routeName, row.vesselName, 'down')} className="text-slate-400 hover:text-white"><ChevronDown size={14} /></button>
                                                </div>
                                            )}
                                            {row.col2.type === 'vessel' && !row.col2.isSubtotal ? (
                                                <div className="w-full h-full flex items-center justify-center relative min-h-[60px]">
                                                    <div className="vertical-text mx-auto px-2 pointer-events-none text-white font-extrabold text-xs">
                                                        {row.col2.name}
                                                    </div>
                                                    <select
                                                        value={row.col2.name}
                                                        onChange={(e) => handleVesselChange(row.clientName, row.routeName, row.col2.name, e.target.value)}
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                    >
                                                        {vesselsList.map(v => (
                                                             <option key={v.vessel_id} value={v.vessel_id} className="bg-slate-800 text-white text-xs">
                                                                {v.vessel_id}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : (
                                                <div className="vertical-text mx-auto px-2">{renderCellContent(row.col2.name, row.col2.isSubtotal)}</div>
                                            )}
                                        </td>
                                    )}
                                    {row.col3 && (
                                        <td rowSpan={row.col3.rowSpan} 
                                            onContextMenu={(e) => { 
                                                if (!row.col3.isSubtotal && row.col3.type) { 
                                                    e.preventDefault(); 
                                                    setContextMenu({ x: e.clientX, y: e.clientY, type: row.col3.type, client: row.clientName, route: row.routeName, vessel: row.vesselName }); 
                                                } 
                                            }}
                                            className={`p-0 border border-slate-200 align-middle relative group ${row.col3.isSubtotal ? 'bg-slate-800 text-amber-400 font-bold' : getCellColor(row.col3.type, row.col3.name) + ' cursor-context-menu'}`}>
                                            {!row.col3.isSubtotal && row.col3.type && (
                                                <div className="absolute top-1 right-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                                    <button onClick={() => handleMove(row.col3.type, row.clientName, row.routeName, row.vesselName, 'up')} className="text-slate-400 hover:text-white"><ChevronUp size={14} /></button>
                                                    <button onClick={() => handleMove(row.col3.type, row.clientName, row.routeName, row.vesselName, 'down')} className="text-slate-400 hover:text-white"><ChevronDown size={14} /></button>
                                                </div>
                                            )}
                                            {row.col3.type === 'vessel' && !row.col3.isSubtotal ? (
                                                <div className="w-full h-full flex items-center justify-center relative min-h-[60px]">
                                                    <div className="vertical-text mx-auto px-2 pointer-events-none text-white font-extrabold text-xs">
                                                        {row.col3.name}
                                                    </div>
                                                    <select
                                                        value={row.col3.name}
                                                        onChange={(e) => handleVesselChange(row.clientName, row.routeName, row.col3.name, e.target.value)}
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                    >
                                                        {vesselsList.map(v => (
                                                            <option key={v.vessel_id} value={v.vessel_id} className="bg-slate-800 text-white text-xs">
                                                                {v.vessel_id}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : (
                                                <div className="vertical-text mx-auto px-2">{renderCellContent(row.col3.name, row.col3.isSubtotal)}</div>
                                            )}
                                        </td>
                                    )}

                                    {/* Celda de Métrica */}
                                    <td className={`py-1 px-2 border border-slate-200 whitespace-nowrap ${row.isSubRow ? 'text-xs text-slate-500' : 'font-semibold text-slate-800'} ${row.metric.isNavSubtotal ? 'font-bold' : ''}`}>
                                        <div className="flex items-center gap-1.5 justify-between">
                                            <div className="flex items-center gap-1">
                                                {row.metric.isExpandableNav && (
                                                    <button 
                                                        onClick={() => toggleNavExpand(row.metric.navExpandKey, row.metric.rowKey)} 
                                                        className="text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                                                    >
                                                        {row.metric.isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                    </button>
                                                )}
                                                <span>{row.metric.name}</span>
                                            </div>
                                            {row.metric.isNaRow && (
                                                <span className="text-[8.5px] bg-slate-200 text-slate-600 px-1 rounded font-normal">N/A</span>
                                            )}
                                        </div>
                                    </td>

                                    {/* Celdas Mensuales */}
                                    {months.filter(m => !hiddenMonths.includes(m)).map((m, idx) => {
                                        const val = row.metric.values ? row.metric.values[idx] : undefined;
                                        return (
                                            <td key={idx} className={`py-1 px-2 border border-slate-200 text-right tabular-nums ${row.isSubRow ? 'text-xs text-slate-500' : val === 0 ? 'text-slate-400' : 'text-slate-800'} ${row.metric.isNavSubtotal ? 'font-bold' : ''}`}>
                                                {row.metric.isCurrency ? formatCurrency(val) : formatNumber(val)}
                                            </td>
                                        );
                                    })}

                                    {/* Celda TOTAL ACUM */}
                                    <td className={`py-1 px-2 border border-slate-200 text-right tabular-nums font-bold bg-slate-100 ${row.metric.isNavSubtotal ? 'font-black bg-slate-200/60' : ''}`}>
                                        {row.metric.isCurrency ? formatCurrency(row.metric.total) : formatNumber(row.metric.total)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Context Menu Clic Derecho */}
            {contextMenu && (
                <div 
                    style={{ top: contextMenu.y, left: contextMenu.x }} 
                    className="fixed bg-white border border-slate-200 shadow-xl rounded-lg py-1 z-50 text-xs w-44 text-slate-700"
                >
                    <button 
                        onClick={() => handleMove(contextMenu.type, contextMenu.client, contextMenu.route, contextMenu.vessel, 'up')} 
                        className="w-full text-left px-3 py-1.5 hover:bg-slate-100 flex items-center gap-2"
                    >
                        <ChevronUp size={14} /> Mover Arriba
                    </button>
                    <button 
                        onClick={() => handleMove(contextMenu.type, contextMenu.client, contextMenu.route, contextMenu.vessel, 'down')} 
                        className="w-full text-left px-3 py-1.5 hover:bg-slate-100 flex items-center gap-2"
                    >
                        <ChevronDown size={14} /> Mover Abajo
                    </button>
                    {onDeleteNode && (
                        <>
                            <div className="h-px bg-slate-200 my-1"></div>
                            <button 
                                onClick={() => onDeleteNode(contextMenu.type, contextMenu.client!, contextMenu.route, contextMenu.vessel)} 
                                className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-600 flex items-center gap-2"
                            >
                                <Trash2 size={14} /> Eliminar {getColumnHeaderLabel(contextMenu.type)}
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
